'use client';

import { Box, Stack, Theme } from '@mui/material';
import {
  ChangeEvent,
  ClipboardEvent,
  FocusEvent,
  KeyboardEvent,
  ReactElement,
  useRef,
} from 'react';
import { AUTHENTICATOR_CODE_PATTERN } from '../PasswordlessLogin/authenticatorCode';

// The "Link sent, one screen" design board (settled 2026-09-17) only ever draws this field on the
// returning-customer sign-in route, under the sentence "Enter the code from your authenticator
// app." ADR 71/72 asks every form input to carry data-field, and this repo already has a name for
// this one spot: "fresh-authenticator-code" (see LinkSentSignInResend.tsx, this field's sole
// caller, composed there as the sign-in route's own resend affordance). It ships as a constant
// rather than a prop - there is exactly one caller this field is for, and a second,
// differently-named caller would be a second occurrence of the same field, which ADR 71/72 exists
// to prevent.
const DATA_FIELD = 'fresh-authenticator-code';

// Stands in for a box that has been individually cleared while a later box still holds a real
// digit - see the "gap" section of the component doc comment for why a plain digit string needs
// one. Any single non-digit character works (digitAt below blanks every non-digit the same way
// regardless of which one it is, and isValidAuthenticatorCode rejects any non-digit character
// identically), so a space was picked for no reason beyond being the most inert thing to find in
// a debugger. Never exported: a caller only needs to know the reported value can become
// not-well-formed, never what specific character produced that, and the exact character is free
// to change later without that being a breaking change for anyone outside this file.
const GAP = ' ';

// Derived from AUTHENTICATOR_CODE_PATTERN's own {n} quantifier rather than a third independent
// literal 6: this keeps authenticatorCode.ts's regex and this component's box count in step WITH
// EACH OTHER, so the two cannot silently drift apart. It does not, on its own, keep either one in
// step with the backend's actual OTP length - if that length ever changes, someone still has to
// edit AUTHENTICATOR_CODE_PATTERN by hand, and nothing here detects a change that never happens.
function boxCountFromPattern(pattern: RegExp): number {
  const match = pattern.source.match(/\\d\{(\d+)\}/);
  if (!match) {
    throw new Error(
      `AuthenticatorCodeInput: could not derive a box count from ${pattern.source} - ` +
        'AUTHENTICATOR_CODE_PATTERN must stay a fixed-length \\d{n} pattern for this to work.',
    );
  }
  return Number(match[1]);
}

const BOX_COUNT = boxCountFromPattern(AUTHENTICATOR_CODE_PATTERN);

export type AuthenticatorCodeInputProps = Readonly<{
  /** The code entered so far: zero to BOX_COUNT characters, box 0 holds value[0], box 1 holds
   *  value[1], and so on. Ordinarily every character is a digit, but a box that has been
   *  individually cleared while a later box still holds one can report a single non-digit
   *  placeholder character in its own position instead - see the component doc comment's "gap"
   *  section for why, and never treat that character as meaningful beyond "not a digit": digitAt
   *  below renders any non-digit the same way (blank), and isValidAuthenticatorCode already
   *  rejects any non-digit character identically. Controlled: this component holds no state of
   *  its own.
   *
   *  Two traps for a caller integrating against this, both easy to get wrong because neither is
   *  visible from the type alone:
   *
   *  1. Length is not completeness. A gap still counts toward `value.length`, so a six-character
   *     value can hold a placeholder and not be a real code. Gating a submit or resend action on
   *     `value.length === 6` - the obvious naive check - enables it on an incomplete code. Test
   *     completeness with `isValidAuthenticatorCode(value)`, never with length.
   *  2. Never sanitise `value` before validating it. The placeholder is whitespace, so a caller
   *     that does `value.trim()` or `value.replace(/\s/g, '')` before checking silently removes
   *     the gap and turns an incomplete code back into a valid-looking one, defeating this
   *     component's whole point at the calling layer. Pass `value` to
   *     `isValidAuthenticatorCode` exactly as received. */
  value: string;
  /** Called with the full concatenated value after every change this component accepts: a digit
   *  typed, a box cleared by Backspace or Delete, or digits pasted. Ordinarily every character
   *  reported is a digit and the string never grows past BOX_COUNT characters - but see `value`'s
   *  own doc comment (including the two integration traps below it) for the one case (an internal
   *  gap) where a single non-digit placeholder character can appear at a specific position
   *  instead, deliberately, so the digits after it
   *  are not silently shifted out of place. */
  onChange: (value: string) => void;
  /** Id of the sentence already on screen that introduces this field (e.g. LinkSentSignInResend's
   *  codePrompt span, identified by that same component's codePromptId - the "Link sent, one
   *  screen" design board, settled 2026-09-17, draws this sentence as "Enter the code from your
   *  authenticator app." directly above the six boxes). Applied to every box as its accessible
   *  name - this component accepts no second, caller-supplied label string, and authors none
   *  itself. */
  'aria-labelledby': string;
  /** Draws the "Link sent, one screen" design board's (settled 2026-09-17) "code rejected" state:
   *  every box's border turns to `theme.palette.text.danger` instead of the resting grey, and
   *  every box carries `aria-invalid="true"` so the failure is not purely visual (see the
   *  component doc comment for why an ARIA state, not a data attribute, is the right vehicle
   *  here). Defaults to false, so no existing call site changes.
   *
   *  This component only ever RENDERS the flag it is told - it never decides a code is wrong
   *  (only the backend can say that), and it never renders the board's separate "Code not
   *  accepted." message: that string sits outside these six boxes and is a caller concern
   *  (ADR 50), same as the rest of this component's contract. Nothing else changes when this is
   *  true: not the size, not the gap, not the digit colour, not any interaction handler. */
  error?: boolean;
  disabled?: boolean;
  'data-testid'?: string;
}>;

/**
 * Six separate digit boxes for a customer typing a live code from their authenticator app, drawn
 * from the "Link sent, one screen" design board (settled 2026-09-17): the "Email sent, sign-in,
 * pressed" column draws it empty, "Email sent, code rejected" draws it filled with 418209. A
 * resend on the returning-customer sign-in route cannot simply repeat the request, because every
 * sign-in link is its own possession check - the backend wants proof the customer still holds
 * their authenticator device right now, which is what these six boxes collect.
 *
 * Controlled, not self-storing: the caller owns `value` and receives every change, matching
 * LinkSentSignInResend's own `freshCode` state (see that component's own doc comment for why a
 * one-time code is never cached or defaulted). This component only ever
 * reports the concatenation of the six boxes - never anything else - and never decides what
 * happens when a code turns out to be wrong: that stays a caller concern (ADR 50). It does,
 * though, render the rejected state it is told about via `error` - deciding is not the same job
 * as rendering, and a caller has no way to reach inside and restyle six boxes from outside. Slot
 * 32's "Code not accepted." message is no part of that: it sits outside these boxes and is drawn
 * by whichever caller sets `error`, not by this component.
 *
 * The six boxes are one logical text value, only visually split into characters, but a customer
 * correcting one wrong digit in an otherwise complete code does not think of it that way: the
 * near-universal move is click the wrong box, Backspace, retype - not click-then-type-over. A
 * plain digit string cannot represent "this one box is now empty, but every later box keeps its
 * own digit in its own position" (there is no character that means "nothing" while still holding
 * a position open), so something has to give. What gives is that clearing a box that still has a
 * later digit after it - a "gap" - reports a single non-digit placeholder character at that exact
 * position instead of collapsing the string: every other box's digit stays exactly where it was,
 * and the reported value simply stops being well-formed (a real backend would refuse it) until the
 * customer retypes that one digit. `digitAt` below renders that placeholder as a blank box, the
 * same as it would render any other non-digit value. This is reserved for a box that still has a
 * later digit after it - clearing the terminal box (the very end of what has been typed so far)
 * still shifts nothing at all to shift: it plainly shortens the string by one character, exactly
 * as backspacing the end of an ordinary text field does.
 *
 * No withClarityTheme wrap, unlike ExpiryClock and LinkSent alongside it: every token this
 * component reaches for (palette.grey, palette.text.primary, theme.semantic.*) already exists on
 * both the default and the Clarity theme, so it renders correctly under whichever theme is
 * ambient rather than forcing one.
 */
export function AuthenticatorCodeInput({
  value,
  onChange,
  'aria-labelledby': ariaLabelledBy,
  error,
  disabled,
  'data-testid': dataTestId,
}: AuthenticatorCodeInputProps): ReactElement {
  const boxRefs = useRef<Array<HTMLInputElement | null>>([]);
  // Set for the duration of a focus() call this component makes on itself (auto-advance after a
  // keystroke, backspace moving back a box, or a paste landing on its last filled box), so
  // handleBoxFocus below can tell that apart from a customer clicking a box directly. This also
  // sidesteps a real staleness trap: the auto-advance call happens synchronously, inside the same
  // event-handler tick that called onChange, before React has re-rendered with the new `value` -
  // so the frontier computed from this render's `value` closure would still be one digit behind
  // and would wrongly bounce the just-advanced focus straight back.
  const isProgrammaticFocusRef = useRef(false);

  function focusBox(index: number) {
    isProgrammaticFocusRef.current = true;
    boxRefs.current[index]?.focus();
    isProgrammaticFocusRef.current = false;
  }

  function digitAt(index: number): string {
    const char = value[index];
    // Blanks two different things identically, on purpose: a caller passing an arbitrary
    // non-digit string (defensive only - onChange itself never reports a letter), and this
    // component's own GAP placeholder (see the component doc comment) for a box that was
    // individually cleared while a later box still holds a digit. Either way, "not a digit" is
    // all that matters for rendering - a blank box, never the placeholder character itself.
    return char && /\d/.test(char) ? char : '';
  }

  function handleBoxChange(index: number, event: ChangeEvent<HTMLInputElement>) {
    // Because of the box's own maxLength (1) and handleBoxFocus selecting any existing digit
    // before a keystroke lands, this is normally a single character - either the new digit or
    // whatever non-digit the customer typed by mistake. Backspace and Delete never reach here at
    // all: handleBoxKeyDown preventDefaults both before the DOM value can change.
    const digit = event.target.value.slice(-1);
    if (!/^\d$/.test(digit)) {
      // Rejected outright, and nothing is done to un-render it: React controls this input's
      // value from `value`/digitAt above, so the next render simply redraws the box exactly as
      // it was, undoing the keystroke on screen without a separate "revert" step.
      return;
    }
    // Replaces the character at this position (or appends, if index is exactly the next empty
    // box) while leaving every other position untouched - one formula covers both, since
    // value.slice(index + 1) is empty when index is already the last character.
    onChange(value.slice(0, index) + digit + value.slice(index + 1));
    if (index + 1 < BOX_COUNT) {
      focusBox(index + 1);
    }
  }

  // Shared by Backspace and Delete: a single-character box has no interior cursor position, so
  // there is no forward/backward distinction for Delete to make that Backspace does not already
  // make - both clear whatever this box holds, exactly the same way.
  function clearBoxAt(index: number) {
    if (digitAt(index) === '') {
      // Already empty - either genuinely nothing here or after (the frontier, index ===
      // value.length: see handleBoxFocus for why nothing further can be focused there), or a gap
      // this component created earlier via the branch below. Either way, pressing the key again
      // moves back one box and clears whatever is there instead of doing nothing: an
      // authenticator code is typed digit by digit with no lookahead, so overshooting past the
      // end (or landing back on a box already fixed) is a customer trying to fix the digit
      // immediately before this one, not a dead end. Recursing (rather than assuming index - 1
      // holds a digit) is what makes this cascade correctly back through more than one gap.
      if (index === 0) {
        return; // nothing before box 0 to fix: a genuine no-op.
      }
      focusBox(index - 1);
      clearBoxAt(index - 1);
      return;
    }
    if (index === value.length - 1) {
      // The terminal box: the very end of what has been typed so far, so there is nothing after
      // it to preserve. This plainly shortens the string by one character, exactly as deleting
      // the last character of an ordinary text field does - see the component doc comment for why
      // this is the one case that still shifts.
      onChange(value.slice(0, index) + value.slice(index + 1));
      return;
    }
    // A later box still holds a digit: clearing this one must not drag it (or anything else after
    // it) left out of its own box - see the component doc comment's "gap" section for why a
    // placeholder, not a shift, is what preserves that.
    onChange(value.slice(0, index) + GAP + value.slice(index + 1));
  }

  function handleBoxKeyDown(index: number, event: KeyboardEvent<HTMLInputElement>) {
    if (event.key !== 'Backspace' && event.key !== 'Delete') {
      return;
    }
    // Owning both keys entirely (rather than letting the browser delete native text and reading
    // the result from onChange) keeps one deterministic code path that does not depend on the
    // box's current text-selection state.
    event.preventDefault();
    clearBoxAt(index);
  }

  function handleBoxFocus(index: number, event: FocusEvent<HTMLInputElement>) {
    if (!isProgrammaticFocusRef.current) {
      // A real customer click/tab, not a move this component made itself. The furthest box that
      // can legitimately be edited is the first empty one (or the last box, once all six are
      // full) - clicking ahead of that is sent back there instead of leaving a hole a plain
      // string value could never represent in the first place.
      const frontier = Math.min(value.length, BOX_COUNT - 1);
      if (index > frontier) {
        focusBox(frontier);
        return;
      }
    }
    // Selects any existing digit so the very next keystroke simply overwrites it: the box's own
    // maxLength=1 would otherwise silently refuse a second character rather than replacing the
    // first, since typing over an unselected single character does not fit inside a full box.
    event.target.select();
  }

  function handlePaste(event: ClipboardEvent<HTMLInputElement>) {
    // Pasting is treated as "the customer copied the whole code from their authenticator app",
    // the realistic scenario this handles, rather than splicing into whichever box happened to be
    // focused: which box was focused when the paste landed does not change what was copied.
    const digits = event.clipboardData.getData('text').replace(/\D/g, '').slice(0, BOX_COUNT);
    if (!digits) {
      // Nothing to paste in (e.g. copied text with no digits at all): let the browser's default
      // paste proceed, which then lands in handleBoxChange as an ordinary non-digit keystroke and
      // is rejected there the same way a single mistyped letter is.
      return;
    }
    event.preventDefault();
    onChange(digits);
    focusBox(Math.min(digits.length, BOX_COUNT - 1));
  }

  return (
    <Stack
      direction="row"
      data-field={DATA_FIELD}
      data-testid={dataTestId}
      sx={(theme: Theme) => ({ gap: theme.semantic.spacing.xs })}
    >
      {Array.from({ length: BOX_COUNT }, (_, index) => (
        // eslint-disable-next-line react/no-array-index-key -- the boxes have no identity beyond
        // their fixed position; BOX_COUNT never changes at runtime.
        <Box
          key={index}
          component="input"
          type="text"
          inputMode="numeric"
          // Not exact digits-only enforcement on its own (see handleBoxChange, which is what
          // actually rejects a non-digit keystroke) - this only steers mobile keyboards towards
          // the numeric pad and hints the same to a password manager or autofill.
          pattern="[0-9]*"
          maxLength={1}
          autoComplete="off"
          value={digitAt(index)}
          disabled={disabled}
          aria-labelledby={ariaLabelledBy}
          // A red border alone is invisible to a screen reader; aria-invalid is the real ARIA
          // state for "this entry failed validation" (ADR 71/72: no invented data attribute
          // duplicating what ARIA already has a name for), so it is what carries the failure to
          // assistive tech. Omitted rather than set to "false" when there is no error, matching
          // the ARIA default (absent == not invalid) and keeping the default render's DOM
          // unchanged from before this prop existed. Deliberately does NOT reference the board's
          // separate "Code not accepted." message via aria-describedby: this component is never
          // given that message's id, since it never renders that message itself (see the
          // component doc comment).
          aria-invalid={error ? 'true' : undefined}
          ref={(el: HTMLInputElement | null) => {
            boxRefs.current[index] = el;
          }}
          onChange={(event: ChangeEvent<HTMLInputElement>) => handleBoxChange(index, event)}
          onKeyDown={(event: KeyboardEvent<HTMLInputElement>) => handleBoxKeyDown(index, event)}
          onFocus={(event: FocusEvent<HTMLInputElement>) => handleBoxFocus(index, event)}
          onPaste={handlePaste}
          sx={(theme: Theme) => ({
            // No token reaches 40: theme.semantic.size['5xs'] is 48 (matching the height below),
            // and every other step is coarser still. Measured directly off the board
            // (getComputedStyle), not derived from a design decision - the same "unreachable
            // value" shape ExpiryClock's own hourglass gap already documents.
            width: '40px',
            height: theme.semantic.size['5xs'],
            // #c5c6c9 exactly. No border token exists, and palette.divider is never set on this
            // theme, so the value ships as a literal grey reference rather than relying on
            // divider's MUI default, which is a different colour. Swapped for
            // theme.palette.text.danger when `error` is set, matching the board's rejected-state
            // rule for what it is: a token reference, not a fixed colour. It resolves to #c13635
            // (Red 500) in light mode but Red 300, #da8686, in dark mode - three steps lighter,
            // not the same value repeated. That lightening is deliberate (this colour sits on a
            // dark surface as a border, and #c13635 loses contrast there), so the token is left
            // to track mode rather than pinned - the opposite call to LinkSent's send-mark icon,
            // which uses palette.permanent.primary specifically because THAT glyph would read as
            // disabled if mode-tracked. permanent.danger is not used here for the same reason:
            // it exists so a status glyph (StatusKnockoutTile's failure state) reads as one fixed
            // red regardless of mode, which is a different job from a border earning contrast on
            // whatever surface it sits on.
            border: `1px solid ${error ? theme.palette.text.danger : theme.palette.grey['200']}`,
            borderRadius: theme.semantic.borderRadius.sm,
            textAlign: 'center',
            // Browsers do not inherit the page's body font onto <input> elements by default; this
            // board renders every digit in the same sans face as the rest of the screen.
            fontFamily: 'inherit',
            // No token reaches 18: the scale steps base (16) then l (20) with nothing between,
            // the same "unreachable value" shape as the 40px width above and ExpiryClock's own
            // hourglass gap. Read directly off the board's own stylesheet
            // (`.codebox.filled{font:600 18px/1 var(--sans)}`), not a screenshot.
            fontSize: '18px',
            fontWeight: theme.semantic.fontWeight.semibold,
            lineHeight: theme.semantic.lineHeight.single,
            // #000000, matching the board's --text-default. NOT --text-primary, which the board
            // uses for its violet brand colour elsewhere - see LinkSent.tsx's send-mark comment
            // for the same trap under the opposite name.
            color: theme.palette.text.primary,
            // A native <input> defaults to a system field background (often opaque) and
            // content-box sizing; the board's own box is transparent and its border sits inside
            // the 40x48 rather than adding to it.
            backgroundColor: 'transparent',
            boxSizing: 'border-box',
            padding: 0,
          })}
        />
      ))}
    </Stack>
  );
}
